import React, {useCallback, useMemo, useState} from "react";
import {Col} from "reactstrap";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import Form from "../../base/form";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../hooks/use-is-mounted";
import {MuiRFF, Yup} from '../../form';
import {DialogActions, DialogContent, DialogTitle, TextField} from "@mui/material";
import FileUtils from "../../../../core/services/file-utils";
import {useBizFileUrlsResolver} from "../../../hooks/use-biz-file-urls-resolver";
import ColorPickerPopover from "../../app-specific/color-picker-popover";
import {ReactComponent as NoColorIcon} from "../../../../assets/images/notifications/no-color.svg";
import {InputFileAcceptMimes, InputFileAcceptTypes} from "../../../../core/constants/enums";
import DropzoneField from "../../text-fields/dropzone-field";
import {CloseRounded} from "@mui/icons-material";
import IconButton from "../../base/button/icon_button";
import Api from "../../../../core/services/api/api";


const formNames = {
    title: 'title',
    color: 'color',
    coverImage: 'coverImage',
}


const schema = Yup.object().shape({
    [formNames.title]: Yup.string().trim().nullable().required(ValidateMessages.required),
    [formNames.color]: Yup.string().trim().nullable(),
    [formNames.coverImage]: Yup.mixed().nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const UpsertProductCategoryDialog = ({open, setOpen, onSuccess, category, industryId, parentId, root}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const bizFileUrls = useBizFileUrlsResolver((t) => t.bizFile);

    const initialValues = useMemo(() => ({
        [formNames.title]: category?.title ?? '',
        [formNames.color]: category?.color,
        [formNames.coverImage]: category?.coverImage,
    }), [category]);

    /**
     * Creates a category with the provided values.
     * @type {(function(*): Promise<undefined|{[formNames.coverImage]: *}>)}
     */
    const createCategory = useCallback(async (values) => {
        const forApi = {
            title: values.title,
            backgroundColor: values.color,
            industryId: industryId,
            parentId: parentId,
            coverImage: values.coverImage,
        }
        setSubmitting(true);
        if (forApi.coverImage instanceof File) {
            const infoOrError = await FileUtils.uploadFile(
                forApi.coverImage,
                async (args) => {
                    return await Api.createPartCategory({
                        ...forApi,
                        fileName: args.fileName,
                        fileSize: args.fileSize,
                        firstChunk: args.chunk
                    }, args.onUploadProgress);
                },
                (data) => data.coverFileName,
                () => void 0,
            )
            if (!isMounted())
                return;
            setSubmitting(false);
            if (infoOrError instanceof Error) {
                return {
                    [formNames.coverImage]: infoOrError.message,
                }
            }
            onSuccess({
                ...infoOrError,
                id: infoOrError.id,
                title: infoOrError.title,
                active: infoOrError.selected,
                editable: !infoOrError.defaultAndNotEditable,
                isPredefined: infoOrError.defaultAndNotEditable,
                color: infoOrError.backgroundColor,
                parentId: parentId,
                coverImage: bizFileUrls.getFilePreviewUrl(infoOrError.coverFileName)
            });
            setOpen(false);
        } else {
            const response = await Api.createPartCategory({
                ...forApi,
                coverImage: undefined,
            })
            if (!isMounted())
                return;
            setSubmitting(false);
            if (response.resultFlag) {
                onSuccess({
                    ...response.data,
                    id: response.data.id,
                    title: response.data.title,
                    active: response.data.selected,
                    editable: !response.data.defaultAndNotEditable,
                    isPredefined: response.data.defaultAndNotEditable,
                    color: response.data.backgroundColor,
                    parentId: parentId,
                    coverImage: undefined,
                });
                setOpen(false);
            }
        }
    }, [industryId, isMounted, onSuccess, parentId, setOpen, bizFileUrls])

    /**
     * Updates the category with the provided values.
     * @type {(function(*): Promise<undefined|{[formNames.coverImage]: *}>)}
     */
    const updateCategory = useCallback(async (values) => {
        const forApi = {
            id: category?.id,
            title: values.title,
            industryId: industryId,
            parentId: parentId,
            backgroundColor: values.color,
            coverImage: values.coverImage,
            updatedProperties: [
                'Title',
                ...(root ? ['BackgroundColor'] : []),
                ...(values.coverImage instanceof File ? ['FirstChunk', "CoverImage"] : []),
            ],
        }
        if (values.title === category.title && values.color === category.color && values.coverImage === category.coverImage) {
            return setOpen(false);
        }
        setSubmitting(true);
        if (forApi.coverImage instanceof File) {
            const infoOrError = await FileUtils.uploadFile(
                forApi.coverImage,
                async (args) => {
                    return await Api.updatePartCategory({
                        ...forApi,
                        coverImage: undefined,
                        fileName: args.fileName,
                        fileSize: args.fileSize,
                        firstChunk: args.chunk
                    }, args.onUploadProgress);
                },
                (data) => data.coverFileName,
                () => void 0,
            )
            if (!isMounted())
                return;
            setSubmitting(false);
            if (infoOrError instanceof Error) {
                return {
                    [formNames.coverImage]: infoOrError.message,
                }
            }
            onSuccess({
                title: forApi.title,
                color: forApi.color,
                parentId: parentId,
                coverImage: infoOrError ? bizFileUrls.getFilePreviewUrl(infoOrError.coverFileName) : undefined,
            });
            setOpen(false);
        } else {
            const response = await Api.updatePartCategory({
                ...forApi,
                fileName: '',
                fileSize: 0,
                firstChunk: '',
            });
            if (!isMounted())
                return;
            setSubmitting(false);
            if (response.resultFlag) {
                onSuccess({
                    ...category,
                    parentId: parentId,
                    title: forApi.title,
                    color: forApi.color,
                });
                setOpen(false);
            }
        }
    }, [category, industryId, isMounted, onSuccess, parentId, root, setOpen, bizFileUrls])


    /**
     * Create a new category and close the dialog
     */
    const submitForm = useCallback(async (values) => {
        if (category) {
            return await updateCategory(values);
        } else {
            return await createCategory(values);
        }
    }, [category, createCategory, updateCategory])


    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
            className={'upsert-category-dialog'}
        >
            <DialogTitle id={'scroll-dialog-title'}>
                {category ? "Edit Category" : "Create New Category"}
            </DialogTitle>
            <DialogContent className={'px-4'} dividers>
                <Form
                    className='d-flex flex-column w-100 mt-3'
                    id={'upsert-category-form'}
                    onSubmit={submitForm}
                    validate={validate}
                    initialValues={initialValues}
                    render={({form, values}) => {
                        const removeCoverImage = async () => {
                            form.change(formNames.coverImage, null)
                            return true;
                        }
                        const removeColor = (e) => {
                            e.stopPropagation();
                            form.change(formNames.color, undefined)
                        }
                        return (
                            <>
                                <Col xs={12} className={'mb-3 px-0'}>
                                    <MuiRFF.TextField
                                        name={formNames.title}
                                        label={'Category Title'}
                                        required={required[formNames.title]}
                                        disabled={submitting}
                                    />
                                </Col>
                                {
                                    root &&
                                    <Col xs={12} className={'mb-3 px-0'}>
                                        <ColorPickerPopover
                                            color={values[formNames.color]}
                                            onImmediateChange={(c) => form.change(formNames.color, c)}
                                            onSelect={(c) => form.change(formNames.color, c)}
                                            anchorOrigin={{vertical: 'bottom', horizontal: 'center',}}
                                            transformOrigin={{vertical: 'bottom', horizontal: 'center',}}
                                        >
                                            {(onEnter) =>
                                                <TextField
                                                    fullWidth
                                                    disabled={submitting}
                                                    variant={'outlined'}
                                                    value={values[formNames.color] ?? ""}
                                                    onClick={onEnter}
                                                    onFocus={onEnter}
                                                    label={'Color'}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <div className={'color-circle'}>
                                                                <NoColorIcon className={'placeholder'}/>
                                                                <div className={'circle'} style={{'--color': values[formNames.color]}}/>
                                                            </div>
                                                        ),
                                                        endAdornment: (
                                                            values[formNames.color] &&
                                                            <IconButton color={'inherit'} onPressed={removeColor}>
                                                                <CloseRounded/>
                                                            </IconButton>
                                                        )
                                                    }}
                                                />
                                            }
                                        </ColorPickerPopover>
                                    </Col>
                                }
                                <Col xs={12} className={'mb-3 px-0'}>
                                    <DropzoneField
                                        useFileAsValue
                                        disableAutoFormChange
                                        required={required[formNames.coverImage]}
                                        name={formNames.coverImage}
                                        accept={[
                                            InputFileAcceptMimes.images,
                                            InputFileAcceptTypes.heic,
                                            InputFileAcceptTypes.heif,
                                        ]}
                                        onChange={(value) => form.change(formNames.coverImage, value)}
                                        fullWidth
                                        disabled={submitting}
                                        remove={removeCoverImage}
                                        initialFileUpload={(args) =>
                                            Api.uploadFile({
                                                file: args.chunk,
                                                fileName: args.fileName,
                                                fileSize: args.fileSize,
                                            }, args.onUploadProgress)
                                        }
                                        uploadFile={(...args) => FileUtils.uploadFile(...args)}
                                    />
                                </Col>
                            </>
                        )
                    }}
                />
            </DialogContent>
            <DialogActions className={'p-0 w-100 d-flex justify-content-between'}>
                <Button
                    disabled={!open || submitting}
                    variant={'dialog'}
                    color={'primary'}
                    type={'button'}
                    onClick={() => setOpen(false)}
                    borderRadius={'0 8px 0 8px'}
                >
                    {submitting ? 'submitting...' : 'Close'}
                </Button>
                <Button
                    disabled={!open || submitting}
                    variant={'dialog'}
                    color={'primary'}
                    type={'submit'}
                    form={'upsert-category-form'}
                    borderRadius={'8px 0 8px 0'}
                >
                    {submitting ? 'submitting...' : category ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


export default UpsertProductCategoryDialog;
